import { AgExpandCellRendererComponent } from 'components/AgGrid/AgExpandCellRendererComponent';
import { AgTooltipHeaderComponent } from 'components/AgGrid/AgTooltipHeaderComponent';
import type { ActKc2SmrPriceItem } from 'gql/graphql';
import { WorkOrderPriceTypeEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef, IRowNode } from 'ag-grid-community';
import type { CellClassParams } from 'ag-grid-community/dist/types/core/entities/colDef';
import { useMemo } from 'react';

interface IUseUerActKC2SmrPriceColumns {
    onExpandDetailsClick: (data?: ActKc2SmrPriceItem) => void;
}

const customHeaderName = () => (
    <span>
        Стоимость
        <div style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            единицы в текущем уровне цен, руб. без НДС
        </div>
    </span>
);

export const getCodeCellClass = (cellClassParams: CellClassParams<ActKc2SmrPriceItem>) => {
    const { data } = cellClassParams;

    if (data?.type === WorkOrderPriceTypeEnum.Uer || data?.type === WorkOrderPriceTypeEnum.Other) {
        return 'textLeft';
    }

    return 'centeredAgGridCell';
};

export const useUerActKC2SmrPriceColumns = (props: IUseUerActKC2SmrPriceColumns) =>
    useMemo<ColDef<ActKc2SmrPriceItem>[] | ColGroupDef<ActKc2SmrPriceItem>[]>(
        () => [
            {
                width: 50,
                cellRenderer: AgExpandCellRendererComponent,
                cellRendererParams: {
                    onClick: props.onExpandDetailsClick,
                    isInvisible: (_: ActKc2SmrPriceItem, node: IRowNode<ActKc2SmrPriceItem>) => !node.rowPinned,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 160,
                cellClass: getCodeCellClass,
            },
            {
                headerName: 'Наименование работ',
                field: 'name',
                flex: 1,
            },
            {
                headerName: 'Ед.изм.',
                field: 'unit',
                width: 100,
            },
            {
                field: 'unitCost',
                width: 180,
                headerComponent: AgTooltipHeaderComponent,
                headerComponentParams: {
                    customHeaderName,
                    displayName: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
                },
                cellClass: 'ag-right-aligned-cell',
                valueFormatter: ({ data }) => formatNumberToRu(data?.unitCost, 2),
            },
            {
                headerName: 'Выполнено работ',
                width: 360,
                children: [
                    {
                        headerName: 'Количество',
                        field: 'costAccepted',
                        width: 180,
                        spanHeaderHeight: true,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: ({ data, node }) =>
                            !node?.rowPinned ? formatNumberToRu(data?.volumeAccepted, 3) : '',
                    },
                    {
                        headerName: 'Стоимость, руб.',
                        field: 'costAccepted',
                        width: 180,
                        spanHeaderHeight: true,
                        cellClass: 'ag-right-aligned-cell',
                        valueFormatter: ({ data }) => formatNumberToRu(data?.costAccepted, 2),
                    },
                ],
            },
        ],
        [props.onExpandDetailsClick],
    );
